import React from 'react';
import {observer} from 'mobx-react';
import {TextSkeleton} from '@udemy-v2/react-reveal-components';
import {useClpPurchaseContext} from '../../context/clp-purchase-context.react-component';
import {CourseSidebarNoTabsContainer} from './course-sidebar-no-tabs-container.react-component';
import {CourseSidebarCollapsibleActionsContainer} from './course-sidebar-collapsible-actions-container.react-component';
import {CourseSidebarTabsContainer} from './course-sidebar-tabs-container.react-component';
import {CourseContentPlacement} from '@/features/course-landing-page/course-landing-page.react-component';
import styles from './course-sidebar-container.module.scss';

export const SIDEBAR_PREVIEW_UI_REGION = 'sidebar.introductionAsset';

export const CourseSidebarContainerSkeleton = () => {
    return (
        <div className={styles.container}>
            <TextSkeleton
                className={styles.skeleton}
                withTitle={true}
                lineCountPerParagraph={3}
                paragraphCount={4}
            />
        </div>
    );
};

interface CourseSidebarContentProps {
    placement: CourseContentPlacement;
    personalContent: React.ReactNode;
    teamsContent: React.ReactNode;
    renderTabContainer: boolean;
    useCollapsiblePurchaseSection?: boolean;
}

/**
 * CourseSidebarContent - handles the reactive loading/content logic
 * This component is wrapped with observer to react to MobX changes
 */
const CourseSidebarContentInternal = ({
    placement,
    personalContent,
    teamsContent,
    renderTabContainer,
    useCollapsiblePurchaseSection,
}: CourseSidebarContentProps) => {
    const {isLoading} = useClpPurchaseContext();

    if (isLoading) {
        return <CourseSidebarContainerSkeleton />;
    }

    if (renderTabContainer) {
        return (
            <CourseSidebarTabsContainer
                personalContent={personalContent}
                teamsContent={teamsContent}
                placement={placement}
            />
        );
    }

    if (useCollapsiblePurchaseSection) {
        return <CourseSidebarCollapsibleActionsContainer />;
    }

    return <CourseSidebarNoTabsContainer placement={placement} />;
};

const CourseSidebarContent = observer(CourseSidebarContentInternal);

interface CourseSidebarContainerProps {
    placement: CourseContentPlacement;
    personalContent: React.ReactNode;
    teamsContent: React.ReactNode;
    renderTabContainer: boolean;
    useCollapsiblePurchaseSection?: boolean;
}

/**
 * CourseSidebarContainer - wraps sidebar purchase content
 * CoursePreviewEntryPoint is now rendered at the parent level to avoid re-renders
 * from design system's useViewport hooks in SidebarPopout/SidebarNormalFlow
 */
export function CourseSidebarContainer({
    placement,
    personalContent,
    teamsContent,
    renderTabContainer,
    useCollapsiblePurchaseSection,
}: CourseSidebarContainerProps) {
    return (
        <CourseSidebarContent
            placement={placement}
            personalContent={personalContent}
            teamsContent={teamsContent}
            renderTabContainer={renderTabContainer}
            useCollapsiblePurchaseSection={useCollapsiblePurchaseSection}
        />
    );
}
